import React, { useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { format } from "date-fns-tz";
import { MemberCertificationStatus } from "Models/member";
import { Member } from "Models/members/member";
import { MemberCertification } from "Models/members/member.certification";
import { Filters, Searches } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";
import { handlesGrid } from "Util/handlesGrid";

import { useCertificationTabActions } from "../../../Tabs/Certifications/useActions";
import { ColumnStatus, ExpireLabel } from "./styles";

interface ICertificationSections {
  member: Member;
}

export const CertificationSections = (props: ICertificationSections) => {
  const { member } = props;
  const { t: translate } = useTranslation();

  const [filters, setFilters] = useState<Filters>({} as Filters);
  const [search, setSearch] = useState<Searches>({} as Searches);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const { handleCurrentPage, handlePageSize } = handlesGrid({
    setPage,
    setPageSize,
  });

  const { getCertifications, getCertificationsCount } = useCertificationTabActions(member.id);

  const { data: certifications, isFetching } = getCertifications(page, pageSize, search, filters);

  const { data: certificationCount } = getCertificationsCount(filters, search);

  const handleSearch = (values: Searches) => {
    setSearch(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    const filtersKeys = Object.getOwnPropertyNames(value);

    setFilters(filtersKeys && filtersKeys?.length ? value : ({} as Filters));
  };

  const columns: Column[] = [
    {
      name: "description",
      title: translate("description"),
    },
    {
      name: "url",
      title: translate("url").toUpperCase(),
      formatText: (value) => value || "-",
    },
    {
      name: "reviewDate",
      title: translate("review"),
      hideSearch: true,
      formatComponent: (value) => {
        if (!value) return "-";

        const isExpired = new Date(value).getTime() < new Date().getTime();

        return (
          <ExpireLabel expired={isExpired}>{format(new Date(value), "dd/MM/yyyy")}</ExpireLabel>
        );
      },
    },
    {
      name: "expireDate",
      title: translate("expiry"),
      hideSearch: true,
      formatComponent: (value) => {
        if (!value) return "-";

        const isExpired = new Date(value).getTime() < new Date().getTime();

        return (
          <ExpireLabel expired={isExpired}>{format(new Date(value), "dd/MM/yyyy")}</ExpireLabel>
        );
      },
    },
    {
      name: "status",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (_, row?: MemberCertification) => {
        if (!row) return "-";

        const backgroundColor =
          row?.status === MemberCertificationStatus.APPROVED || row?.isApproved
            ? colors.success50
            : colors.warning50;
        const color =
          row?.status === MemberCertificationStatus.APPROVED || row?.isApproved
            ? colors.success500
            : colors.warning500;

        return (
          <ColumnStatus>
            <BadgeStatus
              value={row?.status}
              useExternalColor
              backgroundColor={backgroundColor}
              textColor={color}
            />
          </ColumnStatus>
        );
      },
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [
            { label: translate("approved"), value: "approved" },
            { label: translate("waitingApproval"), value: "waiting approval" },
          ],
        },
      ],
      onChange: handleFilter,
    },
  };

  return (
    <ShipeeziNewGrid
      loadingData={isFetching}
      data={certifications || []}
      totalItems={certificationCount || 0}
      columns={columns}
      showSelection={false}
      showSort={false}
      currentPage={page}
      pageSize={pageSize}
      pageSizeList={[10, 20, 40, 60]}
      onChangePageSize={handlePageSize}
      onNextPage={handleCurrentPage}
      onPreviousPage={handleCurrentPage}
      onGoToPage={handleCurrentPage}
      showFooterGrid
      positionActionsColumn="right"
      generalActions={generalActions}
      keyExtractor={(data) => data?.id?.toString() || data?.randomUuid}
      emptyData={translate("noInformationToBeShown")}
    />
  );
};
